import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HuitziBubble — speech bubble in the style of Office 97 Assistant.
 * Renders above-left of the mascot, with a tail pointing toward it.
 *
 * Props:
 *   open       — whether the bubble is visible
 *   message    — text content
 *   actions    — optional array of { label, onClick, variant: 'primary'|'ghost' }
 *   onDismiss  — close button handler
 */
const HuitziBubble = ({ open, message, actions = [], onDismiss }) => (
  <AnimatePresence>
    {open && message && (
      <motion.div
        initial={{ opacity: 0, scale: 0.7, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.7, y: 16 }}
        transition={{ type: 'spring', stiffness: 380, damping: 24 }}
        style={styles.wrapper}
      >
        <div style={styles.bubble}>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              style={styles.close}
              aria-label="Cerrar mensaje"
            >
              ×
            </button>
          )}
          <p style={styles.message}>{message}</p>
          {actions.length > 0 && (
            <div style={styles.actions}>
              {actions.map((action, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={action.onClick}
                  style={action.variant === 'ghost' ? styles.actionGhost : styles.actionPrimary}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Speech-bubble tail pointing down-right toward Huitzi */}
        <svg
          width="32"
          height="20"
          viewBox="0 0 32 20"
          style={styles.tail}
          aria-hidden="true"
        >
          <path
            d="M 0 0 L 32 0 L 16 20 Z"
            fill="white"
            stroke="#10B981"
            strokeWidth="2"
          />
        </svg>
      </motion.div>
    )}
  </AnimatePresence>
);

const styles = {
  wrapper: {
    position: 'absolute',
    bottom: '100%',
    right: 8,
    marginBottom: 12,
    width: 280,
    maxWidth: '85vw',
    pointerEvents: 'auto',
    zIndex: 1,
  },
  bubble: {
    position: 'relative',
    background: 'white',
    border: '2px solid #10B981',
    borderRadius: 16,
    padding: '14px 18px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
  },
  close: {
    position: 'absolute',
    top: 4,
    right: 8,
    background: 'transparent',
    border: 'none',
    fontSize: 20,
    lineHeight: 1,
    color: '#94A3B8',
    cursor: 'pointer',
    padding: 4,
  },
  message: {
    margin: 0,
    fontSize: 14,
    color: '#0F172A',
    lineHeight: 1.45,
    paddingRight: 14,
  },
  actions: {
    display: 'flex',
    gap: 8,
    marginTop: 10,
    flexWrap: 'wrap',
  },
  actionPrimary: {
    background: '#10B981',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionGhost: {
    background: 'transparent',
    color: '#0F766E',
    border: '1.5px solid #14B8A6',
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tail: {
    position: 'absolute',
    right: 24,
    bottom: -18,
  },
};

export default HuitziBubble;
